import { ChangeEvent, forwardRef, Ref, RefAttributes, useEffect, useImperativeHandle, useRef, useState } from "react";

// eslint-disable-next-line no-restricted-imports
import { CircularProgress } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Ratio } from "@simplyk/common";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { useSnackbar } from "notistack";
import { isMobileSafari } from "react-device-detect";
import { FieldValues, Path, useWatch, UseFormReturn } from "react-hook-form";

import { CroppedFile } from "../Crop/Crop";
// eslint-disable-next-line import/no-cycle
import { CropDialog } from "../Crop/CropDialog";

// Dynamically import SelectImageDialog to prevent SSR issues with Unsplash tRPC calls
const SelectImageDialog = dynamic(
  () => import("../SelectImageDialog/SelectImageDialog").then((mod) => mod.SelectImageDialog),
  {
    ssr: false,
  }
);

import { StyledCard, classes } from "./styles";

import { AmplitudeEvents } from "@/constants/amplitude";
import { LIMIT_10_MB } from "@/constants/image";
import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { trpc } from "@/helpers/trpc";
import { isRequired } from "@/helpers/validators";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { useWidth } from "@/hooks/useWidth";
import { ImageDimensions } from "@/types/image";

type Sizes = [100, 100] | [188, 188] | [400, 400] | [800, 800] | [140, 140] | [1024, 576] | [2048, 1152];

export enum ImageType {
  Logo = "logo",
  Banner = "banner",
  Signature = "signature",
  OrganizationLogo = "organizationLogo",
  ItemVisual = "itemVisual",
  TicketBanner = "ticketBanner",
  ProfilePhoto = "profilePhoto",
}

export type RatioSizes =
  | {
      ratio: Ratio.Square;
      sizes: Sizes & ([100, 100] | [188, 188] | [400, 400] | [140, 140] | [800, 800]);
    }
  | {
      ratio: Ratio.Landscape;
      sizes: Sizes & ([1024, 576] | [2048, 1152]);
    };

export interface ImageUploaderHandle {
  setImage: (url: string | null) => void;
}

export interface ImageUploaderProps<T extends FieldValues> {
  className?: string;
  form: UseFormReturn<T>;
  disabled?: boolean;
  importLabel: string;
  name: Path<T>;
  isUploadingName: Path<T>;
  required?: boolean;
  "data-test"?: string;
  allowUnsplash?: boolean;
  maxDimensions: ImageDimensions | undefined;
  imageType: ImageType;
  handleFillWithPlaceholder?: () => Promise<void>;
  amplitudePayload?: Record<string, unknown>;
  disableCropOnMobile?: boolean;
  uploadPreset: "default" | "logo-preset" | "square_800";
  /** Whether to show the delete button when an image is present (default: true) */
  showDeleteButton?: boolean;
}

export const getSizeInBite = (dataUrl: string) => {
  const head = "data:image/png;base64,";
  return Math.round(((dataUrl.length - head.length) * 3) / 4);
};

export const isHeicFormat = (file: File) => {
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    file.name?.toLowerCase().endsWith(".heic") ||
    file.name?.toLowerCase().endsWith(".heif")
  );
};

export const processFileForUpload = async (
  file: File,
  options: {
    onHeicError?: (file: File) => void;
    amplitudePayload?: Record<string, unknown>;
  }
) => {
  const imageDataUrl = URL.createObjectURL(file);

  if (isHeicFormat(file) && !isMobileSafari) {
    options.onHeicError?.(file);
  }

  return imageDataUrl;
};

const detectImageType = (url: string) => {
  if (url.includes(".jpg") || url.includes(".jpeg")) {
    return "image/jpeg";
  }
  if (url.includes(".png")) {
    return "image/png";
  }
  if (url.includes(".webp")) {
    return "image/webp";
  }
  if (url.includes(".jpg")) {
    return "image/jpg";
  }
  if (url.includes(".gif")) {
    return "image/gif";
  }
  if (url.includes(".avif")) {
    return "image/avif";
  }
  if (url.includes(".svg")) {
    return "image/svg";
  }
  return null;
};

export const ImageUploader = forwardRef(ImageUploaderInner) as unknown as <T extends FieldValues>(
  props: ImageUploaderProps<T> & RatioSizes & RefAttributes<ImageUploaderHandle>
) => React.ReactElement<ImageUploaderProps<T> & RatioSizes>;

export function ImageUploaderInner<T extends FieldValues>(
  {
    "data-test": dataTest,
    disabled = false,
    allowUnsplash,
    form,
    name,
    required,
    imageType,
    amplitudePayload,
    disableCropOnMobile = false,
    showDeleteButton = true,
    className,
    importLabel,
    maxDimensions,
    handleFillWithPlaceholder: _handleFillWithPlaceholder,
    ratio,
    isUploadingName,
    uploadPreset = "default",
  }: ImageUploaderProps<T> & RatioSizes,
  ref: Ref<ImageUploaderHandle>
): React.ReactElement<ImageUploaderProps<T> & RatioSizes> {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { isSmallScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();

  const { control, register, setValue } = form;
  const { errors } = form.formState;

  const { mutateAsync: uploadImage } = trpc.uploadImage.useMutation();

  const isUploading = useWatch({ control, name: isUploadingName });

  const disableCrop = disableCropOnMobile && isSmallScreen;

  const defaultFileUrl = form.getValues(name);

  const watchedImageUrl = useWatch({ control, name });
  const [fileUrl, setFileUrl] = useState<string | undefined | null>(defaultFileUrl ?? undefined);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [croppedFile, setCroppedFile] = useState<CroppedFile | undefined>(undefined);
  const [actualImageMetaData, setActualImageMetaData] = useState<
    { width: number; height: number; size: number; extension: string } | undefined
  >(undefined);

  const fillImageMetadata = (url: string) => {
    const isBlob = url.startsWith("data:");
    const extension = (isBlob ? url.split(";")[0].split("image/").pop() : url.split(".").pop()) ?? "none";
    const img = new Image();
    img.src = url;
    img.onload = function () {
      setActualImageMetaData({
        width: img.naturalWidth,
        height: img.naturalHeight,
        extension,
        size: getSizeInBite(url),
      });
    };
  };

  useImperativeHandle(
    ref,
    () => ({
      setImage: (url: string | null) => {
        setFileUrl(url || undefined);
      },
    }),
    []
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const value = useWatch({ control, name });

  const handleCloseCrop = () => {
    setIsCropOpen(false);
  };

  const updateFileUrl = async (image: string | undefined | null) => {
    setValue(isUploadingName as Path<T>, true as never);
    setFileUrl(image);

    if (image && setValue) {
      setValue(name, image as never, { shouldDirty: true });
    }

    if (image) {
      const imageUrl = await uploadImage({ file: image, uploadPreset });
      if (name && setValue) {
        setValue(name, imageUrl as never, { shouldDirty: true });
      }
      fillImageMetadata(image);
    }

    setValue(isUploadingName as Path<T>, false as never);
  };

  // Resets fileUrl only when imageUrl is null, handling banner type switches in Live Form Editor without affecting uploads.
  useEffect(() => {
    if (watchedImageUrl === null) {
      setFileUrl(undefined);
    }
  }, [watchedImageUrl]);

  const handleValidateCrop = (image: string) => {
    updateFileUrl(image);
    setIsCropOpen(false);
    setSelectImageModalOpen(false);
  };

  useEffect(() => {
    if (value === undefined) {
      register(name, isRequired(Boolean(required) && !fileUrl, "common.fileRequired"));
    }
  }, [name, fileUrl, required, value, register]);

  const onFileChange = async (file: File) => {
    const imageDataUrl = await processFileForUpload(file, {
      onHeicError: (file) => {
        enqueueSnackbar(t("common", "heicConversionFailed"), { vibe: "danger" });
        logAmplitudeEvent(AmplitudeEvents.FormHEICImageUploadStarted, {
          file_type: file.type,
          ...amplitudePayload,
        });
      },
      amplitudePayload,
    });

    setCroppedFile({ url: imageDataUrl, type: file.type });
    fillImageMetadata(imageDataUrl);
    setIsCropOpen(true);
  };

  const selectUnsplashImage = (url: string) => {
    const imageType = detectImageType(url) || "image/jpeg";
    setCroppedFile({ url, type: imageType });
    fillImageMetadata(url);
    setIsCropOpen(true);
  };

  const handlePickImage = () => {
    logAmplitudeEvent(AmplitudeEvents.FormImageUploadStarted, {
      original_image_type: imageType,
      ...amplitudePayload,
    });
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  };

  const onInputFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event && event.target ? event.target.files : null;
    if (files && files[0]) {
      if (disableCrop && files[0].size <= LIMIT_10_MB) {
        logAmplitudeEvent(AmplitudeEvents.FormImageUploadStarted, {
          original_image_type: imageType,
          ...amplitudePayload,
        });
        // Skip the crop modal for small screens due to upload issues
        const file = files[0];
        const reader = new FileReader();
        reader.onloadend = async () => {
          if (typeof reader.result === "string" && getSizeInBite(reader.result) <= LIMIT_10_MB) {
            updateFileUrl(reader.result);
            logAmplitudeEvent(AmplitudeEvents.FormImageUploadSubmitted, {
              original_image_type: imageType,
              ...amplitudePayload,
            });
          }
        };
        reader.readAsDataURL(file);
      } else {
        setIsCropOpen(true);
        onFileChange(files[0]);
      }
    }
    event.target.value = ""; // reset the event allow to re-trigger the onChange, if we close the crop modal and re-choose the same file
  };

  const onDelete = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.value = "";
    }
    setFileUrl(null);
    setValue(name, null as never, { shouldDirty: true });
    logAmplitudeEvent(AmplitudeEvents.FormImageDeleted, {
      // disable this props until fixed
      original_image_size: actualImageMetaData?.size,
      original_image_width: actualImageMetaData?.width,
      original_image_height: actualImageMetaData?.height,
      original_image_type: imageType,
      original_image_extension: actualImageMetaData?.extension,
      ...amplitudePayload,
    });
  };

  const [selectImageModalOpen, setSelectImageModalOpen] = useState(false);

  const openSelectImageModal = () => {
    logAmplitudeEvent(AmplitudeEvents.CustomizationClickBannerUnsplash, amplitudePayload);
    setSelectImageModalOpen(true);
  };

  const closeSelectImageModal = () => {
    setSelectImageModalOpen(false);
  };

  const currentDisabled = disabled;
  const ratioValue = ratio === Ratio.Square ? 1 : 16 / 9;

  const [imageUploaderWidth, setImageUploaderWidth] = useState(0);
  const imageUploaderHeight = imageUploaderWidth / ratioValue;
  const imageUploaderRef = useRef(null);
  const { getWidths } = useWidth([imageUploaderRef]);
  useEffect(() => {
    const [columnWidth] = getWidths();
    setImageUploaderWidth(columnWidth);
  }, [getWidths]);

  // Make sure the form is never submitted with an uploading image
  useEffect(() => {
    register(isUploadingName, {
      validate: (value: unknown) => {
        if (value === true) {
          return t("dashboard", "uploadingImage.pleaseWait");
        }
        return true;
      },
    });
  }, [register, isUploadingName, t]);

  return (
    <>
      {isCropOpen && (
        <CropDialog
          isOpen={isCropOpen}
          onCrop={handleValidateCrop}
          onClose={handleCloseCrop}
          croppedFile={croppedFile}
          ratio={ratioValue}
          maxDimensions={maxDimensions}
          imageType={imageType}
          actualImageMetaData={actualImageMetaData}
          fillImageMetadata={fillImageMetadata}
          onFileChange={onFileChange}
          handlePickImage={handlePickImage}
          setCroppedFile={setCroppedFile}
          amplitudePayload={amplitudePayload}
          selectImageModalOpen={selectImageModalOpen}
        />
      )}
      {selectImageModalOpen && (
        <SelectImageDialog
          isOpen={selectImageModalOpen}
          onClose={closeSelectImageModal}
          onSelectImage={selectUnsplashImage}
          amplitudePayload={amplitudePayload}
        />
      )}
      <StyledCard
        variant="outlined"
        fullWidth
        noHover
        className={className}
        classes={{
          root: classNames(classes.uploadCard, classes.noPadding),
          content: classes.noPadding,
        }}
        data-test={dataTest}
        ref={imageUploaderRef}
        style={{ height: imageUploaderHeight }}
      >
        <input
          name={name}
          accept="image/*"
          style={{ display: "none" }}
          id={name}
          type="file"
          onChange={onInputFileChange}
          ref={inputRef}
          disabled={currentDisabled}
        />
        <Grid
          container
          sx={{
            cursor: currentDisabled ? "disabled" : "pointer",
            backgroundImage: `url(${fileUrl})`,
            backgroundSize: "cover",
            height: imageUploaderHeight,
            position: "relative",
          }}
          justifyContent="center"
          alignItems="center"
        >
          {/* Loading overlay */}
          {isUploading && fileUrl && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255,255,255,0.9)",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={20} />
            </Box>
          )}
          {!allowUnsplash && (
            <>
              {!fileUrl && (
                <Grid
                  container
                  justifyContent="center"
                  sx={{ p: 1, position: "relative" }}
                  spacing={1}
                  onClick={handlePickImage}
                >
                  <Grid item>
                    <DashboardButton variant="tonal" vibe="neutral" data-test={`${dataTest}-upload-button`}>
                      {importLabel}
                    </DashboardButton>
                  </Grid>
                </Grid>
              )}
            </>
          )}
          {allowUnsplash && (
            <Grid container justifyContent="center" sx={{ p: 1, position: "relative" }} spacing={1}>
              <Grid item>
                <Grid container spacing={2}>
                  {!fileUrl && (
                    <Grid item container alignItems="center" spacing={1} flexDirection="column" flexWrap="wrap">
                      <Grid item alignSelf="stretch">
                        <DashboardButton
                          variant="tonal"
                          vibe="neutral"
                          onClick={handlePickImage}
                          fullWidth
                          data-test={`${dataTest}-upload-button`}
                        >
                          {importLabel}
                        </DashboardButton>
                      </Grid>

                      <Grid item>
                        <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                          {t("common", "or")}
                        </DashboardTypography>
                      </Grid>
                      <Grid item alignSelf="stretch">
                        <DashboardButton
                          variant="outlined"
                          vibe="neutral"
                          onClick={_handleFillWithPlaceholder ?? openSelectImageModal}
                          fullWidth
                        >
                          {_handleFillWithPlaceholder
                            ? t("dashboard", "fillWithPlaceholderBanner")
                            : t("common", "selectImage")}
                        </DashboardButton>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          {fileUrl && (
            <Grid item container height="100%" alignItems="center" justifyContent="center" spacing={2} padding={1}>
              <Grid item>
                <DashboardButton
                  variant="outlined"
                  vibe="neutral"
                  onClick={allowUnsplash ? openSelectImageModal : handlePickImage}
                  sx={{ backgroundColor: "white" }}
                  data-test={`${dataTest}-replace-button`}
                >
                  {t("common", "replaceImage")}
                </DashboardButton>
              </Grid>

              {showDeleteButton && (
                <Grid item>
                  <DashboardButton
                    variant="outlined"
                    vibe="neutral"
                    onClick={onDelete}
                    sx={{ backgroundColor: "white" }}
                    data-test={`${dataTest}-delete-button`}
                  >
                    {t("common", "deleteImage")}
                  </DashboardButton>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </StyledCard>

      {errors?.[name] && (
        <DashboardTypography variant="body2" sx={{ color: "error.main" }}>
          {String(errors[name]?.message || "")}
        </DashboardTypography>
      )}

      {/* If the user try to upload an image but the image is still uploading, we show an error */}
      {errors?.[isUploadingName] && (
        <DashboardTypography variant="body2" sx={{ color: "error.main" }}>
          {String(errors[isUploadingName]?.message || "")}
        </DashboardTypography>
      )}
    </>
  );
}
